import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Home, CheckCircle } from "lucide-react";

export default function ThankYouPage({ name: nameProp, phone: phoneProp, email: emailProp }) {
  const [details, setDetails] = useState({
    name: nameProp || "",
    phone: phoneProp || "",
    email: emailProp || "",
  });

  useEffect(() => {
    if (nameProp || phoneProp || emailProp) return;

    try {
      const params = new URLSearchParams(window.location.search);
      const fromQuery = {
        name: params.get("name") || "",
        phone: params.get("phone") || "",
        email: params.get("email") || "",
      };

      if (fromQuery.name || fromQuery.phone) {
        setDetails(fromQuery);
        return;
      }

      const stored = sessionStorage.getItem("af_signup_details");
      if (stored) {
        const parsed = JSON.parse(stored);
        setDetails({
          name: parsed.name || "",
          phone: parsed.phone || "",
          email: parsed.email || "",
        });
      }
    } catch (e) {
      // Fall back silently
    }
  }, [nameProp, phoneProp, emailProp]);

  const firstName = details.name ? details.name.split(" ")[0] : "";

  return (
    <div className="aty-root">
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Fraunces:opsz,wght@9..144,500;9..144,600;9..144,700&family=Inter:wght@400;500;600;700&family=JetBrains+Mono:wght@500&display=swap');

        .aty-root {
          --red: #C81E3A;
          --red-deep: #8E1229;
          --ink: #250709;
          --bg-dark: #29070B;
          --bg-darker: #1B0508;
          --paper: #FBEFE7;
          --paper-dim: #F0DCCF;
          --gold: #D9A441;
          --line: rgba(37,7,9,0.1);

          font-family: 'Inter', sans-serif;
          background: var(--paper);
          color: var(--ink);
          min-height: 100vh;
          width: 100%;
          overflow-x: hidden;
          display: flex;
          flex-direction: column;
        }
        .aty-root * { box-sizing: border-box; margin: 0; padding: 0; }
        .aty-root h1, .aty-root h2, .aty-root h3 {
          font-family: 'Fraunces', serif;
          font-weight: 600;
          letter-spacing: -0.01em;
        }

        .aty-topband {
          background: linear-gradient(180deg, var(--bg-dark), var(--bg-darker));
          padding: 28px 48px;
        }
        .aty-logo {
          display: flex;
          align-items: center;
          gap: 10px;
          color: var(--paper);
          font-family: 'Fraunces', serif;
          font-size: 20px;
          font-weight: 700;
          text-decoration: none;
          width: fit-content;
        }
        /* UPDATED LOGO CSS */
        .aty-logo-img {
          height: 50px;
          width: auto;
          object-fit: contain;
          display: block;
        }
        .aty-logo-mark {
          width: 30px;
          height: 30px;
          border-radius: 50%;
          background: linear-gradient(135deg, var(--red), var(--gold));
          display: flex;
          align-items: center;
          justify-content: center;
          font-size: 14px;
          color: var(--ink);
          font-weight: 700;
        }

        .aty-main {
          flex: 1;
          display: flex;
          flex-direction: column;
          align-items: center;
          justify-content: center;
          max-width: 640px;
          margin: 0 auto;
          padding: 60px 24px;
          text-align: center;
        }

        .aty-icon-wrapper {
          width: 80px;
          height: 80px;
          border-radius: 50%;
          background: rgba(200, 30, 58, 0.1);
          display: flex;
          align-items: center;
          justify-content: center;
          margin-bottom: 32px;
          color: var(--red);
        }

        .aty-headline {
          font-size: clamp(32px, 4.5vw, 48px);
          line-height: 1.15;
          margin-bottom: 12px;
        }
        .aty-headline em {
          font-style: normal;
          color: var(--red);
        }

        .aty-sub {
          color: #6b4a45;
          font-size: 18px;
          line-height: 1.7;
          max-width: 500px;
          margin-bottom: 40px;
        }

        .aty-btn {
          font-family: 'Inter', sans-serif;
          font-weight: 600;
          font-size: 14.5px;
          border: none;
          border-radius: 100px;
          padding: 14px 32px;
          cursor: pointer;
          display: inline-flex;
          align-items: center;
          gap: 8px;
          text-decoration: none;
          transition: transform .15s ease, box-shadow .15s ease;
        }
        .aty-btn:active { transform: scale(0.97); }
        .aty-btn-primary {
          background: linear-gradient(135deg, var(--red), var(--red-deep));
          color: var(--paper);
          box-shadow: 0 8px 20px -6px rgba(200,30,58,0.45);
        }
        .aty-btn-primary:hover { 
          box-shadow: 0 10px 26px -6px rgba(200,30,58,0.65);
          transform: translateY(-2px);
        }
        .aty-btn-ghost {
          background: transparent;
          color: var(--ink);
          font-size: 13.5px;
          opacity: 0.7;
        }
        .aty-btn-ghost:hover { opacity: 1; }

        .aty-actions {
          display: flex;
          flex-direction: column;
          gap: 12px;
          align-items: center;
        }

        .aty-divider {
          width: 60px;
          height: 3px;
          background: linear-gradient(90deg, var(--red), var(--gold));
          border-radius: 2px;
          margin: 8px auto 32px;
        }

        @media (max-width: 560px) {
          .aty-topband { padding: 22px 24px; }
          .aty-main { padding: 40px 20px 60px; }
          .aty-headline { font-size: 28px; }
          .aty-sub { font-size: 16px; }
          .aty-icon-wrapper {
            width: 64px;
            height: 64px;
          }
        }
      `}</style>

      <div className="aty-topband">
        <Link to="/" className="aty-logo">
          {/* UPDATED TO USE LOCAL IMAGE */}
          <img src="../../image.png" alt="Anglofone Logo" className="aty-logo-img" />
          
        </Link>
      </div>

      <main className="aty-main">
        <div className="aty-icon-wrapper">
          <CheckCircle size={40} strokeWidth={1.5} />
        </div>

        <h1 className="aty-headline">
          Thank you{firstName ? `, ${firstName}` : ''}!
        </h1>

        <div className="aty-divider" />

        <p className="aty-sub">
          Our course advisor will contact you soon.
        </p>

        <div className="aty-actions">
          <Link to="/" className="aty-btn aty-btn-primary">
            <Home size={16} /> Back to home
          </Link>
        </div>
      </main>
    </div>
  );
}